import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiCheckCircle, FiAlertTriangle, FiBookOpen, FiMapPin } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { MB_AUDIT_REGISTER_DATA } from "./worksReportsData";

const MeasurementBookAuditRegisterReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(MB_AUDIT_REGISTER_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [workOrderFilter, setWorkOrderFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalRecords = data.length;
    const withinLimitCount = data.filter((r) => r.status === "Within Limit").length;
    const exceededCount = data.filter((r) => r.status === "Limit Exceeded Alert").length;
    const uniqueLocationsCount = new Set(data.map((d) => d.subLocation)).size;

    return {
      totalRecords,
      withinLimitCount,
      exceededCount,
      uniqueLocationsCount,
    };
  }, [data]);

  const uniqueWorkOrders = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.workOrderNo).filter(Boolean)));
  }, [data]);

  const getMBChipType = (status) => {
    if (status === "Within Limit") {
      return "success";
    }
    if (status === "Limit Exceeded Alert") {
      return "error";
    }
    return "default";
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "MB Record No.",
        field: "mbRecordNo",
        colId: "mbRecordNo",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              fontFamily: "monospace",
              color: "var(--color-primary, #0b6462)",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Date & Work Order",
        field: "dateRecorded",
        colId: "dateRecorded",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</div>
            <small style={{ color: "#64748b", fontFamily: "monospace" }}>
              {p.data?.workOrderNo}
            </small>
          </div>
        ),
      },
      {
        headerName: "Specific BOQ Item",
        field: "boqItem",
        colId: "boqItem",
        flex: 2,
        minWidth: 220,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</span>,
      },
      {
        headerName: "Sub-Location / Grid",
        field: "subLocation",
        colId: "subLocation",
        flex: 1.6,
        minWidth: 180,
      },
      {
        headerName: "Measurement (L × B × H)",
        field: "dimensions",
        colId: "dimensions",
        flex: 1.7,
        minWidth: 180,
        cellRenderer: (p) => (
          <span style={{ fontFamily: "monospace", fontSize: "11px", color: "#334155" }}>
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Measured This Entry",
        field: "qtyThisEntry",
        colId: "qtyThisEntry",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0284c7" }}>
            {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Cumulative Measured",
        field: "cumulativeQty",
        colId: "cumulativeQty",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => {
          const isOver = p.value > (p.data?.woLimit || 0);
          return (
            <span
              style={{
                fontWeight: 700,
                color: isOver ? "#dc2626" : "#0f172a",
              }}
            >
              {p.value !== undefined ? Number(p.value).toLocaleString() : "0"}
            </span>
          );
        },
      },
      {
        headerName: "WO Limit",
        field: "woLimit",
        colId: "woLimit",
        flex: 1.1,
        minWidth: 110,
        valueFormatter: (p) => (p.value !== undefined ? Number(p.value).toLocaleString() : "—"),
      },
      {
        headerName: "Audit Status",
        field: "status",
        colId: "status",
        flex: 1.4,
        minWidth: 160,
        cellRenderer: (p) => <StatusChip status={p.value} type={getMBChipType(p.value)} />,
      },
      {
        headerName: "Recorded & Verified By",
        field: "recordedBy",
        colId: "recordedBy",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => (
          <div>
            <div style={{ color: "#334155", fontSize: "12px" }}>{p.value}</div>
            <small style={{ color: "#16a34a", fontWeight: 600 }}>
              Verified: {p.data?.verifiedBy}
            </small>
          </div>
        ),
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.mbRecordNo && row.mbRecordNo.toLowerCase().includes(q)) ||
          (row.workOrderNo && row.workOrderNo.toLowerCase().includes(q)) ||
          (row.boqItem && row.boqItem.toLowerCase().includes(q)) ||
          (row.subLocation && row.subLocation.toLowerCase().includes(q)) ||
          (row.recordedBy && row.recordedBy.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }

      if (workOrderFilter !== "all" && row.workOrderNo !== workOrderFilter) {
        return false;
      }

      if (dateRange.from && row.dateRecorded && row.dateRecorded < dateRange.from) {
        return false;
      }
      if (dateRange.to && row.dateRecorded && row.dateRecorded > dateRange.to) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, workOrderFilter, dateRange]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Measurement_Book_Audit_Register_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("MB audit register exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL MB RECORDS AUDITED"
            value={`${kpis.totalRecords} Entries`}
            subtitle="Site measurement log entries"
            icon={FiBookOpen}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="WITHIN AGREED WO LIMIT"
            value={`${kpis.withinLimitCount} Records`}
            subtitle="Verified against contract allowances"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="LIMIT EXCEEDED ALERTS"
            value={`${kpis.exceededCount} Discrepancies`}
            subtitle="Exceeds authorized contract scope"
            icon={FiAlertTriangle}
            color="#dc2626"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="MONITORED SITE LOCATIONS"
            value={`${kpis.uniqueLocationsCount} Locations`}
            subtitle="Active grid zones & floor levels"
            icon={FiMapPin}
            color="#0284c7"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="MB Quantity Audit Report"
            >
              MB Quantity Audit Report
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search MB record, item, location..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All MB Statuses</option>
                  <option value="Within Limit">Within Limit</option>
                  <option value="Limit Exceeded Alert">Limit Exceeded Alert</option>
                </select>
                <label>Status</label>
              </div>
            </div>

            {/* Work Order Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={workOrderFilter}
                  onChange={(e) => setWorkOrderFilter(e.target.value)}
                >
                  <option value="all">All Work Orders</option>
                  {uniqueWorkOrders.map((w) => (
                    <option key={w} value={w}>
                      {w}
                    </option>
                  ))}
                </select>
                <label>Work Order</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeasurementBookAuditRegisterReport;
